import React, { useState, useEffect, useMemo } from 'react';
import { Search, UserCog, Mail, Phone, AlertCircle, RefreshCw, Users, GraduationCap, Building2, X, Calendar, Eye, Key, KeyRound, Copy, EyeOff, CheckCircle, AlertTriangle, Loader2, Archive, ArchiveRestore, Trash2, Edit, Clock, FileEdit, ClipboardCheck, Plus } from 'lucide-react';
import {
  fetchStaffUsers,
  fetchManagementPermissions,
  fetchStaffStats,
  adminResetPassword,
  adminResetStaffTempPassword,
  archiveStaff,
  restoreStaff,
  deleteStaff,
  type StaffUser,
  type ManagementPermissions,
  type StaffStats,
  type AdminResetStaffTempPasswordResponse,
  getStaffCountsByAcademicYear,
} from '@/services/userService';
import { getPendingValidations, getValidationRequestByUserId, type PendingValidation } from '@/services/validationService';
import { getPendingChangeRequests, type ChangeRequest } from '@/services/changeRequestService';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import { ViewStaffModal } from '@/shared/components/users/ViewStaffModal';
import { StaffFiltersPanel, type StaffFilters } from '@/shared/components/users/StaffFiltersPanel';
import { DirectEditStaffProfileModal } from '@/shared/components/users/DirectEditStaffProfileModal';
import { ProfileValidationModal } from '@/shared/components/users/ProfileValidationModal';
import { AdminChangeRequestsCard } from '@/shared/components/users/AdminChangeRequestsCard';
import { CreateStaffModal } from '@/shared/components/users/CreateStaffModal';

// Country phone codes mapping
const COUNTRY_PHONE_CODES: Record<string, { dialCode: string; flag: string }> = {
  'MA': { dialCode: '+212', flag: '🇲🇦' },
  'FR': { dialCode: '+33', flag: '🇫🇷' },
  'US': { dialCode: '+1', flag: '🇺🇸' },
  'GB': { dialCode: '+44', flag: '🇬🇧' },
  'DE': { dialCode: '+49', flag: '🇩🇪' },
  'ES': { dialCode: '+34', flag: '🇪🇸' },
  'IT': { dialCode: '+39', flag: '🇮🇹' },
  'BE': { dialCode: '+32', flag: '🇧🇪' },
  'NL': { dialCode: '+31', flag: '🇳🇱' },
  'PT': { dialCode: '+351', flag: '🇵🇹' },
  'DZ': { dialCode: '+213', flag: '🇩🇿' },
  'TN': { dialCode: '+216', flag: '🇹🇳' },
  'SA': { dialCode: '+966', flag: '🇸🇦' },
  'AE': { dialCode: '+971', flag: '🇦🇪' },
  'EG': { dialCode: '+20', flag: '🇪🇬' },
  'SN': { dialCode: '+221', flag: '🇸🇳' },
  'CI': { dialCode: '+225', flag: '🇨🇮' },
  'ML': { dialCode: '+223', flag: '🇲🇱' },
  'MR': { dialCode: '+222', flag: '🇲🇷' },
};

// Format phone number with country code (defaults to Morocco if no country specified)
const formatPhoneWithCountry = (phone?: string, countryCode?: string): string => {
  if (!phone) return '';

  // If phone already starts with + or 00, it already has the dial code - don't add another
  if (phone.startsWith('+') || phone.startsWith('00')) {
    // Try to find the country flag based on the dial code in the phone
    for (const [, data] of Object.entries(COUNTRY_PHONE_CODES)) {
      if (phone.startsWith(data.dialCode)) {
        return `${data.flag} ${phone}`;
      }
    }
    return phone;
  }

  // Default to Morocco (MA) if no country code is provided
  const effectiveCountryCode = countryCode || 'MA';
  const country = COUNTRY_PHONE_CODES[effectiveCountryCode];
  if (country) {
    return `${country.flag} ${country.dialCode} ${phone}`;
  }
  return phone;
};

type RoleFilter = 'ALL' | 'TEACHERS' | string;

/**
 * Finance Staff View Page - Read-only view of staff and professors
 * For Financial Manager (Responsable Financier) role
 *
 * Features:
 * - View-only mode (no validation/approval actions)
 * - Two stat cards: Total Personnel, Total Professors
 * - Simplified table: Nom, Rôle, Contact only
 * - Search and filters functionality
 * - Campus filtering for multi-campus institutions
 */
export const FinanceStaffViewPage: React.FC = () => {
  const tokens = useAuthStore((state) => state.tokens);
  const currentUser = useAuthStore((state) => state.user);
  const { hasPermission } = usePermissions();

  // Permission checks
  const canCreateStaff = hasPermission(Permission.STAFF_CREATE);
  const canResetPassword = hasPermission(Permission.STAFF_RESET_PASSWORD);
  const canArchiveStaff = hasPermission(Permission.STAFF_ARCHIVE);
  const canDeleteStaff = hasPermission(Permission.STAFF_DELETE);
  const canEditProfile = hasPermission(Permission.STAFF_EDIT_PROFILE);
  const canValidateProfile = hasPermission(Permission.STAFF_VALIDATE_PROFILE);
  const canValidateModifications = hasPermission(Permission.STAFF_VALIDATE_MODIFICATIONS);

  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [permissions, setPermissions] = useState<ManagementPermissions | null>(null);
  const [stats, setStats] = useState<StaffStats | null>(null);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  // Headcount per year, counting what the list actually shows.
  const [academicYearCounts, setAcademicYearCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffUser | null>(null);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('ALL');

  // Advanced filters state (checkbox-based multi-select)
  const [advancedFilters, setAdvancedFilters] = useState<StaffFilters>({});
  const [allStaff, setAllStaff] = useState<StaffUser[]>([]); // Store unfiltered data for filter options
  const [currentAcademicYear, setCurrentAcademicYear] = useState<AcademicYear | null>(null);
  const [academicYearInitialized, setAcademicYearInitialized] = useState(false);

  // Password reset modal - Step 1: Choose method (email or temp password)
  const [showResetPasswordChoiceModal, setShowResetPasswordChoiceModal] = useState(false);
  const [staffForPasswordReset, setStaffForPasswordReset] = useState<StaffUser | null>(null);

  // Password reset modal - Step 2: Email-based reset
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [staffToResetPassword, setStaffToResetPassword] = useState<StaffUser | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  // Password reset modal - Staff temp password
  const [showResetStaffTempPasswordModal, setShowResetStaffTempPasswordModal] = useState(false);
  const [staffTempResetResult, setStaffTempResetResult] = useState<AdminResetStaffTempPasswordResponse | null>(null);
  const [resettingStaffTempPassword, setResettingStaffTempPassword] = useState(false);
  const [resetStaffTempPasswordError, setResetStaffTempPasswordError] = useState<string | null>(null);
  const [showStaffTempPassword, setShowStaffTempPassword] = useState(false);
  const [copiedStaffField, setCopiedStaffField] = useState<'email' | 'password' | null>(null);

  // Archive modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [staffToArchive, setStaffToArchive] = useState<StaffUser | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<StaffUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Edit profile modal state
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [staffToEdit, setStaffToEdit] = useState<StaffUser | null>(null);

  // Validation and change requests state
  const [pendingValidations, setPendingValidations] = useState<PendingValidation[]>([]);
  const [pendingChangeRequests, setPendingChangeRequests] = useState<ChangeRequest[]>([]);
  const [showChangeRequestsSection, setShowChangeRequestsSection] = useState(false);
  const [profileStatusFilter, setProfileStatusFilter] = useState<'ALL' | 'PENDING_VALIDATION'>('ALL');

  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedValidationId, setSelectedValidationId] = useState<string | null>(null);

  // Create staff modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Load current academic year and set as default filter
  useEffect(() => {
    const loadCurrentAcademicYear = async () => {
      if (!tokens?.accessToken) {
        setAcademicYearInitialized(true);
        return;
      }

      try {
        const years = await fetchAcademicYears(tokens.accessToken);
        const current = years.find(y => y.isCurrent);
        if (current) {
          setCurrentAcademicYear(current);
          setAcademicYears(years);
          // Pre-select current academic year in filters
          setAdvancedFilters(prev => ({
            ...prev,
            academicYearId: [current.id]
          }));
        }
        setAcademicYearInitialized(true);
      } catch (error) {
        console.error('Error loading academic years:', error);
        setAcademicYearInitialized(true);
      }
    };

    loadCurrentAcademicYear();
  }, [tokens?.accessToken]);

  // Load data after academic year is initialized
  useEffect(() => {
    if (academicYearInitialized) {
      loadData(currentAcademicYear ? true : false);
    }
  }, [academicYearInitialized]);

  const loadData = async (applyFilters = false) => {
    try {
      setLoading(true);
      setError(null);

      // Prepare filters for API call
      const filtersToApply = applyFilters ? advancedFilters : undefined;

      // Always fetch unfiltered data for filter options when applying filters
      const [staffResponse, allStaffResponse, permissionsData, statsData, validationsData, changeRequestsData, yearsData] = await Promise.all([
        fetchStaffUsers(filtersToApply),
        applyFilters ? fetchStaffUsers() : Promise.resolve({ data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 0 } }), // Fetch all staff for filter options
        fetchManagementPermissions(),
        fetchStaffStats().catch(() => null), // Don't fail if stats can't be loaded
        getPendingValidations().catch(() => []), // Don't fail if validations can't be loaded
        getPendingChangeRequests().catch(() => []), // Don't fail if change requests can't be loaded
        tokens?.accessToken ? fetchAcademicYears(tokens.accessToken).catch(() => []) : Promise.resolve([]),
      ]);

      // Extract data arrays from paginated responses
      const staffData = staffResponse.data;
      const allStaffData = allStaffResponse.data;

      setStaff(staffData);
      setPermissions(permissionsData);
      setStats(statsData);
      setAcademicYears(yearsData);

        // Per-year counts come from the server: they follow the filter's own
        // rule (active staff always listed, the year only scopes archived ones).
        getStaffCountsByAcademicYear()
          .then((rows) =>
            setAcademicYearCounts(
              Object.fromEntries(rows.map((r) => [r.academicYearId, r.count])),
            ),
          )
          .catch(() => undefined);

      // Store unfiltered data for filter options
      if (applyFilters && allStaffData.length > 0) {
        setAllStaff(allStaffData);
      } else if (!applyFilters) {
        setAllStaff(staffData);
      }

      // Filter validations to only include staff (non-student, non-parent)
      const staffValidations = validationsData.filter((v: PendingValidation) =>
        v.user.role !== 'STUDENT' && v.user.role !== 'PARENT'
      );
      setPendingValidations(staffValidations);

      // Filter change requests to only include staff (non-student, non-parent)
      const staffChangeRequests = changeRequestsData.filter((r: ChangeRequest) =>
        r.user?.role !== 'STUDENT' && r.user?.role !== 'PARENT'
      );
      setPendingChangeRequests(staffChangeRequests);
    } catch (err) {
      console.error('Error fetching staff data:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Reload when advanced filters change
  useEffect(() => {
    const hasAdvancedFilters =
      (advancedFilters.role && advancedFilters.role.length > 0) ||
      (advancedFilters.academicYearId && advancedFilters.academicYearId.length > 0) ||
      (advancedFilters.campusId && advancedFilters.campusId.length > 0);

    if (hasAdvancedFilters) {
      loadData(true); // Apply filters
    } else if (allStaff.length > 0) {
      setStaff(allStaff); // Reset to all staff
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  // Build role names map from permissions
  const roleNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    if (permissions?.creatableRoles) {
      permissions.creatableRoles.forEach((r) => {
        map.set(r.role, r.nameFr);
      });
    }
    // Add current role info as well
    if (permissions?.currentRole && permissions?.currentRoleInfo) {
      map.set(permissions.currentRole, permissions.currentRoleInfo.nameFr);
    }
    return map;
  }, [permissions]);

  const filteredStaff = useMemo(() => {
    return staff.filter(s => {
      // Filter by profile status (for validation filtering)
      if (profileStatusFilter === 'PENDING_VALIDATION') {
        if (s.profileSubmissionStatus !== 'PENDING_VALIDATION') {
          return false;
        }
      }

      // Filter by role
      if (roleFilter !== 'ALL') {
        if (roleFilter === 'TEACHERS') {
          // Show only teachers (TEACHER role or staff who are also professors)
          const isTeacher = s.role === 'TEACHER';
          const isAlsoProfessor = s.isAlsoProfessor === true;
          if (!isTeacher && !isAlsoProfessor) {
            return false;
          }
        } else {
          // Specific role filter
          if (s.role !== roleFilter) {
            return false;
          }
        }
      }

      // Then filter by search
      const searchLower = searchQuery.toLowerCase();
      return (
        s.firstName.toLowerCase().includes(searchLower) ||
        s.lastName.toLowerCase().includes(searchLower) ||
        s.email.toLowerCase().includes(searchLower)
      );
    });
  }, [staff, searchQuery, roleFilter, profileStatusFilter]);

  // Count pending validations
  const pendingValidationCount = stats?.pendingValidation ??
    staff.filter(s => s.profileSubmissionStatus === 'PENDING_VALIDATION').length;

  // Find validation request ID for a staff member
  const getValidationRequestId = (userId: string): string | null => {
    const validation = pendingValidations.find(v => v.userId === userId);
    return validation?.id || null;
  };

  // Handle opening validation modal
  const handleOpenValidation = async (userId: string) => {
    // First try from cache
    let validationId = getValidationRequestId(userId);

    // If not in cache, fetch from API
    if (!validationId) {
      try {
        validationId = await getValidationRequestByUserId(userId);
      } catch (err) {
        console.error('Error fetching validation request:', err);
      }
    }

    if (validationId) {
      setSelectedValidationId(validationId);
      setShowValidationModal(true);
    } else {
      console.error('No validation request found for user:', userId);
    }
  };

  // Handle validation complete
  const handleValidationComplete = () => {
    loadData(true); // Refresh to update statuses
  };

  // Handle change request processed
  const handleChangeRequestProcessed = () => {
    loadData(true); // Refresh data after processing
  };

  const getRoleName = (role: string): string => {
    const roleInfo = permissions?.creatableRoles.find(r => r.role === role);
    return roleInfo?.nameFr || roleNamesMap.get(role) || role;
  };

  const handleViewStaff = (member: StaffUser) => {
    setSelectedStaff(member);
    setShowViewModal(true);
  };

  // Password reset handlers - Show choice modal
  const handleResetPasswordClick = (member: StaffUser, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setStaffForPasswordReset(member);
    setShowResetPasswordChoiceModal(true);
  };

  // Handle choice: reset staff password via email
  const handleChooseStaffEmailReset = () => {
    if (!staffForPasswordReset) return;
    setShowResetPasswordChoiceModal(false);
    setStaffToResetPassword(staffForPasswordReset);
    setShowResetPasswordModal(true);
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
  };

  // Handle choice: reset staff password with temp password
  const handleChooseStaffTempPasswordReset = () => {
    if (!staffForPasswordReset) return;
    setShowResetPasswordChoiceModal(false);
    setStaffToResetPassword(staffForPasswordReset);
    setShowResetStaffTempPasswordModal(true);
    setStaffTempResetResult(null);
    setResetStaffTempPasswordError(null);
    setShowStaffTempPassword(false);
    setCopiedStaffField(null);
  };

  const handleCancelPasswordChoice = () => {
    setShowResetPasswordChoiceModal(false);
    setStaffForPasswordReset(null);
  };

  const handleConfirmResetPassword = async () => {
    if (!staffToResetPassword) return;

    setResettingPassword(true);
    setResetPasswordError(null);
    try {
      const result = await adminResetPassword(staffToResetPassword.id);
      setResetPasswordSuccess(result.message);
      setTimeout(() => {
        setShowResetPasswordModal(false);
        setStaffToResetPassword(null);
        setStaffForPasswordReset(null);
        setResetPasswordSuccess(null);
      }, 2000);
    } catch (error) {
      console.error('Error resetting password:', error);
      setResetPasswordError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCancelResetPassword = () => {
    setShowResetPasswordModal(false);
    setStaffToResetPassword(null);
    setStaffForPasswordReset(null);
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
  };

  // Staff temp password reset handlers
  const handleConfirmResetStaffTempPassword = async () => {
    if (!staffToResetPassword) return;

    setResettingStaffTempPassword(true);
    setResetStaffTempPasswordError(null);
    try {
      const result = await adminResetStaffTempPassword(staffToResetPassword.id);
      setStaffTempResetResult(result);
    } catch (error) {
      console.error('Error resetting staff temp password:', error);
      setResetStaffTempPasswordError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResettingStaffTempPassword(false);
    }
  };

  const handleCancelResetStaffTempPassword = () => {
    setShowResetStaffTempPasswordModal(false);
    setStaffToResetPassword(null);
    setStaffForPasswordReset(null);
    setStaffTempResetResult(null);
    setResetStaffTempPasswordError(null);
    setShowStaffTempPassword(false);
    setCopiedStaffField(null);
  };

  const copyStaffCredential = async (text: string, field: 'email' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStaffField(field);
      setTimeout(() => setCopiedStaffField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Archive handlers
  const handleArchiveClick = (member: StaffUser, e: React.MouseEvent) => {
    e.stopPropagation();
    setStaffToArchive(member);
    setShowArchiveModal(true);
  };

  const handleConfirmArchive = async () => {
    if (!staffToArchive) return;

    setArchiving(true);
    try {
      if (staffToArchive.status === 'WITHDRAWN') {
        await restoreStaff(staffToArchive.id);
      } else {
        await archiveStaff(staffToArchive.id);
      }
      await loadData(true);
      setShowArchiveModal(false);
      setStaffToArchive(null);
    } catch (error) {
      console.error('Error archiving/restoring staff:', error);
    } finally {
      setArchiving(false);
    }
  };

  // Edit profile handlers
  const handleEditProfileClick = (member: StaffUser, e: React.MouseEvent) => {
    e.stopPropagation();
    setStaffToEdit(member);
    setShowEditProfileModal(true);
  };

  const handleEditProfileSuccess = () => {
    loadData(true); // Refresh the list after successful edit
  };

  // Delete handlers
  const handleDeleteClick = (member: StaffUser, e: React.MouseEvent) => {
    e.stopPropagation();
    setStaffToDelete(member);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!staffToDelete) return;

    setDeleting(true);
    try {
      await deleteStaff(staffToDelete.id);
      await loadData(true);
      setShowDeleteModal(false);
      setStaffToDelete(null);
    } catch (error) {
      console.error('Error deleting staff:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setRoleFilter('ALL');
    setSearchQuery('');
    setAdvancedFilters({});
  };

  // Check if any filter is active
  const hasAdvancedFiltersActive =
    (advancedFilters.role && advancedFilters.role.length > 0) ||
    (advancedFilters.academicYearId && advancedFilters.academicYearId.length > 0) ||
    (advancedFilters.campusId && advancedFilters.campusId.length > 0);

  const hasActiveFilters = roleFilter !== 'ALL' || hasAdvancedFiltersActive;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show error state if API call failed
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-800 mb-4">{error}</p>
        <button
          onClick={() => loadData()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <RefreshCw size={16} />
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Personnel</h1>
          <p className="text-gray-500 mt-1">
            Consultez les membres du personnel et les professeurs
          </p>
        </div>
        {canCreateStaff && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={20} />
            Ajouter un membre
          </button>
        )}
      </div>

      {/* Dashboard Stats Cards - Dynamic based on permissions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Staff Card - Clickable */}
        <button
          onClick={() => {
            setRoleFilter('ALL');
            setProfileStatusFilter('ALL');
            setShowChangeRequestsSection(false);
          }}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            roleFilter === 'ALL' && profileStatusFilter === 'ALL' && !showChangeRequestsSection
              ? 'border-primary-500 ring-2 ring-primary-200'
              : 'border-gray-200 hover:border-primary-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Users size={24} className="text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Personnel</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.totalStaff ?? staff.length}
              </p>
            </div>
          </div>
        </button>

        {/* Total Professors Card - Clickable */}
        <button
          onClick={() => {
            setRoleFilter(roleFilter === 'TEACHERS' ? 'ALL' : 'TEACHERS');
            setProfileStatusFilter('ALL');
            setShowChangeRequestsSection(false);
          }}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            roleFilter === 'TEACHERS' && !showChangeRequestsSection
              ? 'border-purple-500 ring-2 ring-purple-200'
              : 'border-gray-200 hover:border-purple-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${roleFilter === 'TEACHERS' && !showChangeRequestsSection ? 'bg-purple-200' : 'bg-purple-100'}`}>
              <GraduationCap size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Professeurs</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.totalProfessors ?? 0}
              </p>
            </div>
          </div>
        </button>

        {/* Pending Validations Card - Only shown if user can validate profiles */}
        {canValidateProfile && (
          <button
            onClick={() => {
              setProfileStatusFilter(profileStatusFilter === 'PENDING_VALIDATION' ? 'ALL' : 'PENDING_VALIDATION');
              setRoleFilter('ALL');
              setShowChangeRequestsSection(false);
            }}
            className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
              profileStatusFilter === 'PENDING_VALIDATION' && !showChangeRequestsSection
                ? 'border-orange-500 ring-2 ring-orange-200'
                : 'border-gray-200 hover:border-orange-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingValidationCount > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                <Clock size={24} className={pendingValidationCount > 0 ? 'text-orange-600' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Dossiers à valider</p>
                <p className={`text-2xl font-bold ${pendingValidationCount > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                  {pendingValidationCount}
                </p>
              </div>
            </div>
          </button>
        )}

        {/* Demandes de modification Card - Only shown if user can validate modifications */}
        {canValidateModifications && (
          <button
            onClick={() => {
              setShowChangeRequestsSection(prev => !prev);
              if (!showChangeRequestsSection) {
                setProfileStatusFilter('ALL');
                setRoleFilter('ALL');
              }
            }}
            className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
              showChangeRequestsSection
                ? 'border-blue-500 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingChangeRequests.length > 0 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <FileEdit size={24} className={pendingChangeRequests.length > 0 ? 'text-blue-600' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Demandes de modification</p>
                <p className={`text-2xl font-bold ${pendingChangeRequests.length > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                  {pendingChangeRequests.length}
                </p>
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Search Row */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Advanced Filters Panel (checkboxes) */}
      <StaffFiltersPanel
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        staffData={allStaff.length > 0 ? allStaff : staff}
        roleNames={roleNamesMap}
        availableAcademicYears={academicYears}
        academicYearCounts={academicYearCounts}
      />

      {/* Active Filter Badges */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500">Filtres actifs:</span>

          {/* Role filter badge */}
          {roleFilter !== 'ALL' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
              <GraduationCap size={14} />
              {roleFilter === 'TEACHERS' ? 'Professeurs' : getRoleName(roleFilter)}
              <button
                onClick={() => setRoleFilter('ALL')}
                className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          {/* Advanced filters badges */}
          {advancedFilters.role && advancedFilters.role.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <Users size={14} />
              {advancedFilters.role.length === 1
                ? roleNamesMap.get(advancedFilters.role[0]) || advancedFilters.role[0]
                : `${advancedFilters.role.length} rôles`}
              <button
                onClick={() => setAdvancedFilters({ ...advancedFilters, role: undefined })}
                className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          {advancedFilters.academicYearId && advancedFilters.academicYearId.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <Calendar size={14} />
              {advancedFilters.academicYearId.length === 1
                ? (academicYears.find(ay => ay.id === advancedFilters.academicYearId![0])?.name ||
                   (currentAcademicYear?.id === advancedFilters.academicYearId[0] ? currentAcademicYear.name : advancedFilters.academicYearId[0]))
                : `${advancedFilters.academicYearId.length} années`}
              <button
                onClick={() => setAdvancedFilters({ ...advancedFilters, academicYearId: undefined })}
                className="ml-1 hover:bg-green-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          {advancedFilters.campusId && advancedFilters.campusId.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
              <Building2 size={14} />
              {advancedFilters.campusId.length === 1
                ? (allStaff.flatMap(s => s.campuses || []).find(c => c.id === advancedFilters.campusId![0])?.name || advancedFilters.campusId[0])
                : `${advancedFilters.campusId.length} campus`}
              <button
                onClick={() => setAdvancedFilters({ ...advancedFilters, campusId: undefined })}
                className="ml-1 hover:bg-amber-200 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </span>
          )}

          <span className="text-sm text-gray-500">({filteredStaff.length} résultat{filteredStaff.length > 1 ? 's' : ''})</span>
        </div>
      )}

      {/* Change Requests Section - Shown when clicking the card */}
      {showChangeRequestsSection && canValidateModifications && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileEdit size={20} className="text-blue-600" />
              Demandes de modification en attente
            </h2>
            <button
              onClick={() => setShowChangeRequestsSection(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
          <AdminChangeRequestsCard
            userTypeFilter="staff"
            onRequestReviewed={handleChangeRequestProcessed}
          />
        </div>
      )}

      {/* Staff Table - Simplified: Nom, Rôle, Contact + Actions if permissions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                {(canResetPassword || canArchiveStaff || canDeleteStaff || canEditProfile || canValidateProfile) && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan={(canResetPassword || canArchiveStaff || canDeleteStaff || canEditProfile || canValidateProfile) ? 4 : 3} className="px-6 py-12 text-center text-gray-500">
                    <UserCog className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    {hasActiveFilters ? (
                      <>
                        <p>Aucun membre du personnel avec ces filtres</p>
                        <button
                          onClick={clearFilters}
                          className="text-sm text-primary-600 hover:underline mt-2"
                        >
                          Afficher tout le personnel
                        </button>
                      </>
                    ) : (
                      <p>Aucun membre du personnel trouvé</p>
                    )}
                  </td>
                </tr>
              ) : (
                filteredStaff.map((member) => (
                  <tr
                    key={member.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewStaff(member)}
                  >
                    {/* Nom - First name + Last name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-700 font-medium text-sm">
                            {member.firstName[0]}{member.lastName[0]}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {member.firstName} {member.lastName}
                        </div>
                      </div>
                    </td>

                    {/* Rôle */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {getRoleName(member.role)}
                      </span>
                    </td>

                    {/* Contact - Phone + Email */}
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {member.phone && (
                          <div className="flex items-center gap-1 text-gray-900">
                            <Phone size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">{formatPhoneWithCountry(member.phone, member.phoneCountry)}</span>
                          </div>
                        )}
                        {member.email && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <Mail size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate max-w-[180px]">{member.email}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Actions column */}
                    {(canResetPassword || canArchiveStaff || canDeleteStaff || canEditProfile || canValidateProfile) && (
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewStaff(member);
                            }}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Voir les détails"
                          >
                            <Eye size={18} />
                          </button>
                          {/* Validate Profile Button - Only shown for pending validation */}
                          {canValidateProfile && member.profileSubmissionStatus === 'PENDING_VALIDATION' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenValidation(member.id);
                              }}
                              className="p-2 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Valider le profil"
                            >
                              <ClipboardCheck size={18} />
                            </button>
                          )}
                          {canEditProfile && (
                            <button
                              onClick={(e) => handleEditProfileClick(member, e)}
                              className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Modifier le profil"
                            >
                              <Edit size={18} />
                            </button>
                          )}
                          {canResetPassword && member.id !== currentUser?.id && (
                            <button
                              onClick={(e) => handleResetPasswordClick(member, e)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Réinitialiser le mot de passe"
                            >
                              <KeyRound size={18} />
                            </button>
                          )}
                          {canArchiveStaff && member.id !== currentUser?.id && (
                            <button
                              onClick={(e) => handleArchiveClick(member, e)}
                              className={`p-2 rounded-lg transition-colors ${
                                member.status === 'WITHDRAWN'
                                  ? 'text-green-500 hover:text-green-600 hover:bg-green-50'
                                  : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                              }`}
                              title={member.status === 'WITHDRAWN' ? 'Restaurer' : 'Archiver (Démission)'}
                            >
                              {member.status === 'WITHDRAWN' ? <ArchiveRestore size={18} /> : <Archive size={18} />}
                            </button>
                          )}
                          {canDeleteStaff && member.id !== currentUser?.id && (
                            <button
                              onClick={(e) => handleDeleteClick(member, e)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Staff Modal - Read-only */}
      <ViewStaffModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedStaff(null);
        }}
        staff={selectedStaff}
        permissions={permissions}
        // No onRoleUpdated - view only mode
      />

      {/* Password Reset Choice Modal */}
      {showResetPasswordChoiceModal && staffForPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Key size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Réinitialiser le mot de passe
                  </h3>
                  <p className="text-sm text-gray-500">
                    {staffForPasswordReset.firstName} {staffForPasswordReset.lastName}
                  </p>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                Choisissez la méthode de réinitialisation du mot de passe :
              </p>

              <div className="space-y-3">
                {/* Option 1: Send email */}
                <button
                  onClick={handleChooseStaffEmailReset}
                  className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
                >
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                    <Mail size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Envoyer un email</p>
                    <p className="text-sm text-gray-500">
                      Un lien de réinitialisation sera envoyé par email
                    </p>
                  </div>
                </button>

                {/* Option 2: Generate temp password */}
                <button
                  onClick={handleChooseStaffTempPasswordReset}
                  className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all group text-left"
                >
                  <div className="p-2 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition-colors">
                    <KeyRound size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Mot de passe temporaire</p>
                    <p className="text-sm text-gray-500">
                      Générer un mot de passe temporaire à communiquer
                    </p>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={handleCancelPasswordChoice}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Temp Password Reset Modal */}
      {showResetStaffTempPasswordModal && staffToResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <KeyRound size={24} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Mot de passe temporaire
                  </h3>
                  <p className="text-sm text-gray-500">
                    {staffToResetPassword.firstName} {staffToResetPassword.lastName}
                  </p>
                </div>
              </div>

              {!staffTempResetResult ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <div className="flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-1">Attention</p>
                        <p>
                          Un mot de passe temporaire sera généré. Le membre du personnel devra
                          créer un nouveau mot de passe lors de sa prochaine connexion.
                        </p>
                      </div>
                    </div>
                  </div>

                  {resetStaffTempPasswordError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                      <AlertCircle size={18} />
                      <span className="text-sm">{resetStaffTempPasswordError}</span>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={handleCancelResetStaffTempPassword}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      disabled={resettingStaffTempPassword}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleConfirmResetStaffTempPassword}
                      disabled={resettingStaffTempPassword}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {resettingStaffTempPassword ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Génération...
                        </>
                      ) : (
                        <>
                          <KeyRound size={16} />
                          Générer
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium">Mot de passe généré avec succès!</p>
                        <p className="mt-1">
                          Communiquez ces identifiants au membre du personnel.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-lg px-4 py-2 font-mono text-sm">
                          {staffTempResetResult.email || 'Non disponible'}
                        </div>
                        {staffTempResetResult.email && (
                          <button
                            onClick={() => copyStaffCredential(staffTempResetResult.email!, 'email')}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Copier"
                          >
                            {copiedStaffField === 'email' ? (
                              <CheckCircle size={18} className="text-green-600" />
                            ) : (
                              <Copy size={18} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mot de passe temporaire
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 font-mono text-sm">
                          {showStaffTempPassword ? staffTempResetResult.tempPassword : '••••••••'}
                        </div>
                        <button
                          onClick={() => setShowStaffTempPassword(!showStaffTempPassword)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title={showStaffTempPassword ? 'Masquer' : 'Afficher'}
                        >
                          {showStaffTempPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        <button
                          onClick={() => copyStaffCredential(staffTempResetResult.tempPassword, 'password')}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Copier"
                        >
                          {copiedStaffField === 'password' ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : (
                            <Copy size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Note:</span> Le membre du personnel sera obligé de
                      changer ce mot de passe lors de sa prochaine connexion.
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleCancelResetStaffTempPassword}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Password Reset Modal */}
      {showResetPasswordModal && staffToResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Réinitialiser par email
                  </h3>
                  <p className="text-sm text-gray-500">
                    {staffToResetPassword.firstName} {staffToResetPassword.lastName}
                  </p>
                </div>
              </div>

              {resetPasswordSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <p className="font-medium">{resetPasswordSuccess}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">
                    Un email de réinitialisation sera envoyé à :
                  </p>
                  <div className="bg-gray-100 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
                    <Mail size={16} className="text-gray-500" />
                    <span className="font-medium">{staffToResetPassword.email}</span>
                  </div>

                  {resetPasswordError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                      <AlertCircle size={18} />
                      <span className="text-sm">{resetPasswordError}</span>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={handleCancelResetPassword}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      disabled={resettingPassword}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleConfirmResetPassword}
                      disabled={resettingPassword}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {resettingPassword ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Envoi...
                        </>
                      ) : (
                        <>
                          <Mail size={16} />
                          Envoyer
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && staffToArchive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${staffToArchive.status === 'WITHDRAWN' ? 'bg-green-100' : 'bg-orange-100'}`}>
                  {staffToArchive.status === 'WITHDRAWN' ? (
                    <ArchiveRestore size={24} className="text-green-600" />
                  ) : (
                    <Archive size={24} className="text-orange-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {staffToArchive.status === 'WITHDRAWN' ? 'Restaurer le compte' : 'Archiver le compte'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {staffToArchive.firstName} {staffToArchive.lastName}
                  </p>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                {staffToArchive.status === 'WITHDRAWN'
                  ? 'Êtes-vous sûr de vouloir restaurer ce compte ? Le membre du personnel pourra à nouveau accéder au système.'
                  : 'Êtes-vous sûr de vouloir archiver ce compte ? Le membre du personnel sera marqué comme démissionnaire et ne pourra plus accéder au système.'}
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowArchiveModal(false);
                    setStaffToArchive(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={archiving}
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmArchive}
                  disabled={archiving}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${
                    staffToArchive.status === 'WITHDRAWN'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {archiving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : staffToArchive.status === 'WITHDRAWN' ? (
                    <ArchiveRestore size={16} />
                  ) : (
                    <Archive size={16} />
                  )}
                  {archiving ? 'En cours...' : staffToArchive.status === 'WITHDRAWN' ? 'Restaurer' : 'Archiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && staffToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Supprimer le compte</h3>
                  <p className="text-sm text-gray-500">
                    {staffToDelete.firstName} {staffToDelete.lastName}
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">Attention - Action irréversible</p>
                    <p>
                      Cette action supprimera définitivement le compte de ce membre du personnel.
                      Toutes ses données seront perdues.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setStaffToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={deleting}
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {deleting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  {deleting ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {staffToEdit && (
        <DirectEditStaffProfileModal
          isOpen={showEditProfileModal}
          onClose={() => {
            setShowEditProfileModal(false);
            setStaffToEdit(null);
          }}
          staffUserId={staffToEdit.id}
          staffName={`${staffToEdit.firstName} ${staffToEdit.lastName}`}
          onSuccess={handleEditProfileSuccess}
        />
      )}

      {/* Profile Validation Modal */}
      {showValidationModal && selectedValidationId && (
        <ProfileValidationModal
          isOpen={showValidationModal}
          onClose={() => {
            setShowValidationModal(false);
            setSelectedValidationId(null);
          }}
          validationRequestId={selectedValidationId}
          onValidationComplete={handleValidationComplete}
        />
      )}

      {/* Create Staff Modal */}
      {canCreateStaff && (
        <CreateStaffModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData(true);
          }}
          permissions={permissions}
        />
      )}
    </div>
  );
};

export default FinanceStaffViewPage;
